import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserManagementData, getAllUsers, searchUsers, toggleUserStatus, triggerPasswordReset, ClubMembership, getUserClubMemberships, getUserLicense, updateUserLicense, removeUserLicense } from '../services/userManagement';
import { updatePlayer } from '../services/players';
import LinkGuestModal from './LinkGuestModal';
import './UserManagement.css';

interface UserManagementProps {
  className?: string;
}

const UserManagement: React.FC<UserManagementProps> = ({ className }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserManagementData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserManagementData | null>(null);
  const [showClubModal, setShowClubModal] = useState(false);
  const [selectedUserClubs, setSelectedUserClubs] = useState<ClubMembership[]>([]);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [editingLicenseUser, setEditingLicenseUser] = useState<UserManagementData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserManagementData | null>(null);
  const [showLinkGuestModal, setShowLinkGuestModal] = useState(false);
  const [linkingUser, setLinkingUser] = useState<UserManagementData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      let userData;
      if (searchTerm.trim()) {
        userData = await searchUsers(searchTerm, includeDeleted);
      } else {
        userData = await getAllUsers(includeDeleted);
      }
      setUsers(userData);
      
      // Load club counts for each user asynchronously
      loadClubCounts(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Load club counts for users
  const loadClubCounts = async (usersData: UserManagementData[]) => {
    try {
      const updatedUsers = await Promise.all(
        usersData.map(async (user) => {
          const clubMemberships = await getUserClubMemberships(user.id);
          const license = await getUserLicense(user.id);
          return {
            ...user,
            total_clubs: clubMemberships.length,
            club_memberships: clubMemberships,
            license: license
          };
        })
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error loading club counts:', error);
      // Don't set error here as the main data is already loaded
    }
  };

  useEffect(() => {
    loadUsers();
  }, [includeDeleted]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDeleteClick = (user: UserManagementData) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      const success = await toggleUserStatus(userToDelete.id, userToDelete.playerUUID, userToDelete.status === 'deleted');
      if (success) {
        await loadUsers(); // Reload users
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        setError('Failed to update user status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      await triggerPasswordReset(email);
      alert('Password reset email sent successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    }
  };

  const handleEditUser = (user: UserManagementData) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleShowClubs = async (user: UserManagementData) => {
    try {
      const clubMemberships = await getUserClubMemberships(user.id);
      setSelectedUserClubs(clubMemberships);
      setSelectedUserName(`${user.preName} ${user.name}`);
      setShowClubModal(true);
    } catch (error) {
      console.error('Error loading club memberships:', error);
      setError('Failed to load club memberships');
    }
  };

  const handleChangeLicense = (user: UserManagementData) => {
    setEditingLicenseUser(user);
    setShowLicenseModal(true);
  };

  const handleSaveUser = async (updatedUser: UserManagementData) => {
    try {
      await updatePlayer(updatedUser.id, {
        name: updatedUser.name,
        preName: updatedUser.preName,
        pin: updatedUser.pin,
        language: updatedUser.language
      });
      setShowEditModal(false);
      setEditingUser(null);
      await loadUsers(); // Reload users
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    return (
      <span className={`status-badge status-${status}`}>
        {status === 'active' ? 'Active' : 'Deleted'}
      </span>
    );
  };

  return (
    <div className={`user-management ${className || ''}`}>
      <div className="user-management-header">
        <h2>User Management</h2>
        
        <div className="user-management-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            Show deleted users
          </label>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="user-management-content">
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Guest</th>
                  <th>Status</th>
                  <th>Games</th>
                  <th>Clubs</th>
                  <th>License</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={user.status === 'deleted' ? 'deleted' : ''}>
                    <td>
                      <div className="user-info">
                        <div className="user-name">
                          {user.preName} {user.name}
                        </div>
                        <div className="user-pin">
                          PIN: {user.pin || 'Not set'}
                        </div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td className="text-center">
                      {user.is_guest ? (
                        <span className="guest-badge-label" title="Guest player">👤</span>
                      ) : (
                        <span className="no-guest" title="Regular user">—</span>
                      )}
                    </td>
                    <td>{getStatusBadge(user.status)}</td>
                    <td>{user.total_games || 0}</td>
                    <td>
                      <button 
                        className="clubs-link"
                        onClick={() => handleShowClubs(user)}
                        disabled={!user.total_clubs || user.total_clubs === 0}
                      >
                        {user.total_clubs || 0}
                      </button>
                    </td>
                    <td>
                      <button 
                        className="license-badge-button"
                        onClick={() => handleChangeLicense(user)}
                        title="Click to change license"
                      >
                        <span className={`license-badge license-${user.license?.license_type || 'basic'}`}>
                          {user.license?.license_type || 'basic'}
                        </span>
                      </button>
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        {user.is_guest ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setLinkingUser(user);
                              setShowLinkGuestModal(true);
                            }}
                            title="Link this guest to a user account"
                          >
                            Link to User
                          </button>
                        ) : (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handlePasswordReset(user.email)}
                          >
                            Reset Password
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${
                            user.status === 'deleted' ? 'btn-success' : 'btn-warning'
                          }`}
                          onClick={() => handleDeleteClick(user)}
                        >
                          {user.status === 'deleted' ? 'Restore' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {users.length === 0 && (
              <div className="no-users">
                {searchTerm ? 'No users found matching your search.' : 'No users found.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onSave={handleSaveUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
        />
      )}
      
      <ClubMembershipModal
        isOpen={showClubModal}
        onClose={() => {
          setShowClubModal(false);
          setSelectedUserClubs([]);
          setSelectedUserName('');
        }}
        userName={selectedUserName}
        clubs={selectedUserClubs}
      />
      
      <LicenseChangeModal
        isOpen={showLicenseModal}
        user={editingLicenseUser}
        onSave={async (licenseType, expiresAt) => {
          if (editingLicenseUser) {
            try {
              if (licenseType === 'basic') {
                await removeUserLicense(editingLicenseUser.id);
              } else {
                await updateUserLicense(editingLicenseUser.id, licenseType, expiresAt);
              }
              
              // Reload user data to reflect changes
              await loadUsers();
              setShowLicenseModal(false);
              setEditingLicenseUser(null);
            } catch (error) {
              console.error('Error updating license:', error);
              setError('Failed to update license');
            }
          }
        }}
        onClose={() => {
          setShowLicenseModal(false);
          setEditingLicenseUser(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {userToDelete.status === 'deleted' 
                  ? t('userManagement.restoreUser') 
                  : t('userManagement.deleteUser')
                }
              </h3>
              <button className="close-button" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p>
                {userToDelete.status === 'deleted' 
                  ? t('userManagement.confirmRestore', { name: `${userToDelete.preName} ${userToDelete.name}` })
                  : t('userManagement.confirmDelete', { name: `${userToDelete.preName} ${userToDelete.name}` })
                }
              </p>
              {userToDelete.status !== 'deleted' && (
                <p className="warning-text">
                  {t('userManagement.deleteWarning')}
                </p>
              )}
            </div>
            
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className={`btn ${userToDelete.status === 'deleted' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleConfirmDelete}
              >
                {userToDelete.status === 'deleted' ? t('common.restore') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Guest Modal */}
      {showLinkGuestModal && linkingUser && linkingUser.playerUUID && (
        <LinkGuestModal
          userUuid={linkingUser.playerUUID}
          userName={`${linkingUser.preName} ${linkingUser.name}`}
          onClose={() => {
            setShowLinkGuestModal(false);
            setLinkingUser(null);
          }}
          onSuccess={() => {
            setShowLinkGuestModal(false);
            setLinkingUser(null);
            loadUsers(); // Reload to reflect changes
          }}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
interface EditUserModalProps {
  user: UserManagementData;
  onSave: (user: UserManagementData) => void;
  onClose: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    preName: user.preName,
    pin: user.pin || '',
    language: user.language || 'de'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...user,
      ...formData
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Edit User: {user.email}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-content">
          <div className="form-group">
            <label htmlFor="preName">First Name</label>
            <input
              type="text"
              id="preName"
              name="preName"
              value={formData.preName}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="name">Last Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="pin">PIN</label>
            <input
              type="text"
              id="pin"
              name="pin"
              value={formData.pin}
              onChange={handleChange}
              placeholder="Optional PIN"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="language">Language</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Club Membership Modal Component
interface ClubModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  clubs: ClubMembership[];
}

const ClubMembershipModal: React.FC<ClubModalProps> = ({ isOpen, onClose, userName, clubs }) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="modal-overlay">
      <div className="modal club-modal">
        <div className="modal-header">
          <h2>Club Memberships - {userName}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          {clubs.length === 0 ? (
            <p>No club memberships found.</p>
          ) : (
            <div className="clubs-list">
              {clubs.map((club) => (
                <div key={club.club_id} className="club-item">
                  <div className="club-info">
                    <h3>{club.club_name}</h3>
                    <div className="club-details">
                      <span className={`role-badge role-${club.role}`}>
                        {club.role}
                      </span>
                      <span className="join-date">
                        Joined: {formatDate(club.joined_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// License Change Modal Component
interface LicenseChangeModalProps {
  isOpen: boolean;
  user: UserManagementData | null;
  onSave: (licenseType: string, expiresAt?: string) => void;
  onClose: () => void;
}

const LicenseChangeModal: React.FC<LicenseChangeModalProps> = ({ isOpen, user, onSave, onClose }) => {
  const [licenseType, setLicenseType] = useState<string>('basic');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [showExpiration, setShowExpiration] = useState<boolean>(false);

  useEffect(() => {
    if (user && isOpen) {
      setLicenseType(user.license?.license_type || 'basic');
      setExpiresAt(user.license?.expires_at || '');
      setShowExpiration(user.license?.license_type !== 'basic');
    }
  }, [user, isOpen]);

  const handleLicenseTypeChange = (newType: string) => {
    setLicenseType(newType);
    setShowExpiration(newType !== 'basic');
    if (newType === 'basic') {
      setExpiresAt('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(licenseType, showExpiration ? expiresAt : undefined);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="modal-overlay">
      <div className="modal license-modal">
        <div className="modal-header">
          <h2>Change License - {user.preName} {user.name}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-content">
          <div className="form-group">
            <label htmlFor="licenseType">License Type</label>
            <select
              id="licenseType"
              value={licenseType}
              onChange={(e) => handleLicenseTypeChange(e.target.value)}
              required
            >
              <option value="basic">Basic (Free)</option>
              <option value="club">Club (Premium)</option>
            </select>
          </div>
          
          {showExpiration && (
            <div className="form-group">
              <label htmlFor="expiresAt">Expiration Date (Optional)</label>
              <input
                type="date"
                id="expiresAt"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                placeholder="Leave empty for no expiration"
              />
              <small className="form-help">
                Leave empty for no expiration date
              </small>
            </div>
          )}
          
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update License
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
